import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Grid,
} from "@mui/material";
import "./index.css";
import { FaStar } from "react-icons/fa";
import { FaCheck } from "react-icons/fa6";
import { MdAssignment, MdPeople, MdCheckCircle } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomLocationSearch from "../IconInput/customInput";
import {
  LOCATION_CONSTANT,
  service_booking_keys,
  SERVICE_DETAIL_PAGE_CONSTANT,
  steps_constants,
} from "../../store/constants";
import { setJobsSteps } from "../../store/slices/ui_control_slice";
import { themeOrange } from "../../utils/colorTheme";
import logo_white from "../../assets/logo_2.webp";
import ButtonComp from "../common/ButtonComp";
import { RxCross2 } from "react-icons/rx";
import CustomModal from "../common/CustomModal/CustomModal";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import { GoCheckCircleFill } from "react-icons/go";

const FirstCard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openVendorModal, setOpenVendorModal] = useState(false);
  const [location, setLocation] = useState("");
  const [locationErr, setLocationErr] = useState("");
  const [selectedCard, setSelectedCard] = useState("lower");
  const [openMemberShipModal, setOpenMemberShipModal] = useState(false);
  const [openRatesModal, setOpenRatesModal] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [fromSelectBox, setFromSelectBox] = useState(false);

  const { user } = useSelector((state) => state.userAuth);
  const { get_service_by_id_data } = useSelector((state) => state.serviceManage);

  // Generate unique but consistent review data per service ID
  const getServiceReview = (serviceId) => {
    if (!serviceId) return { reviews: 4.8, jobs: 150 };
    const ratings = [4.8, 4.8, 4.8, 4.7, 4.8, 4.9, 4.8];
    const rating = ratings[(serviceId * 3 + 7) % ratings.length];
    const jobs = 74 + ((serviceId * 43 + 17) % 289); // range: 74–362
    return { reviews: rating, jobs };
  };
  const currentReview = getServiceReview(get_service_by_id_data?.service?.id);

  useEffect(() => {
    const storedCard = localStorage.getItem(service_booking_keys.PRICE);
    if (storedCard) setSelectedCard(storedCard === "member" ? "lower" : "upper");
  }, [user?.subscription_id]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const _handleCardSelection = (card) => {
    if (+user?.user_role === 2) {
      setOpenVendorModal(!openVendorModal);
    } else {
      setSelectedCard(card);
      localStorage.setItem(service_booking_keys.PRICE, card === "lower" ? "member" : "user");
    }
  };

  const jobs_steps = localStorage.getItem(steps_constants.JOBS_STEP);

  const _handleStart = () => {
    localStorage.setItem(service_booking_keys.PRICE, selectedCard === "upper" ? "user" : "member");
  };

  useEffect(() => {
    const _getLocations = JSON.parse(localStorage.getItem(LOCATION_CONSTANT))?.reverse() || [];
    setAllLocations([..._getLocations]);
    if (_getLocations?.length > 0) { setFromSelectBox(true); setLocation(_getLocations?.[0]); }
  }, []);

  const _handleClick = async () => {
    setLocationErr("");
    try {
      if (!location) { setLocationErr("Please select a location."); return; }
      const results = await geocodeByPlaceId(location);
      if (!results?.length) { setLocationErr("Please select a valid location."); return; }
      let streetNumber = "";
      let formattedAddress = results[0].formatted_address;
      results[0].address_components.forEach((c) => { if (c.types.includes("street_number")) streetNumber = c.long_name; });
      if (!streetNumber) { setLocationErr("Street number is missing in the address."); return; }
      if (selectedCard === "lower") {
        setOpenMemberShipModal(true); _handleStart();
        localStorage.setItem(service_booking_keys.LOCATION, formattedAddress);
      } else {
        _handleStart();
        localStorage.setItem(service_booking_keys.LOCATION, formattedAddress);
        dispatch(setJobsSteps({ value: "1" }));
      }
    } catch (error) { setLocationErr("Error fetching location details."); }
  };

  const _handleSelectedLocation = (value) => { setLocation(value.value.place_id); setFromSelectBox(false); };

  useEffect(() => { if (location !== "") setLocationErr(""); }, [location]);

  const servicePrice = get_service_by_id_data?.service?.price || 0;
  const memberPrice = Math.max(0, servicePrice - 25);

  return (
    <Stack sx={{ p: { xs: 3, md: 4 } }}>
      {/* Service Title */}
      <Typography
        sx={{
          fontSize: { xs: "24px", md: "32px" },
          fontWeight: 700,
          color: "#1a1a1a",
          mb: 1,
        }}
      >
        {get_service_by_id_data?.service?.name || "Service"}
      </Typography>

      {/* Rating */}
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <Box
          sx={{
            backgroundColor: "#162f77",
            color: "white",
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <FaStar size={12} />
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
            {currentReview.reviews}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "13px", color: "#666" }}>
          of {currentReview.jobs} reviews
        </Typography>
      </Stack>

      {/* Book Your Service Section */}
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#1a1a1a",
          mb: 1,
        }}
      >
        Book your service instantly
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: "#666",
          lineHeight: 1.7,
          mb: 3,
        }}
      >
        {get_service_by_id_data?.service?.description || "Technicians will evaluate and provide the optimal solution for your faulty appliance. For gas stoves and ranges, please choose the Gas Services category."}
      </Typography>

      {/* Popular Uses */}
      {get_service_by_id_data?.service?.used_for?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#1a1a1a",
              mb: 2,
            }}
          >
            Popular uses for this service
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {get_service_by_id_data?.service?.used_for?.map((item, i) => (
              <Stack key={i} direction="row" alignItems="center" gap={1}>
                <FaCheck size={14} color={themeOrange} />
                <Typography sx={{ fontSize: "14px", color: "#333" }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* Help Link */}
      <Typography sx={{ fontSize: "14px", color: "#666", mb: 4 }}>
        Not sure if this is the right service for you?{" "}
        <span
          onClick={() => navigate("/help-center")}
          style={{ color: themeOrange, cursor: "pointer", fontWeight: 500 }}
        >
          Help center
        </span>
      </Typography>

      {/* How Our Booking Works */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: { xs: "18px", md: "22px" }, fontWeight: 700, color: "#1a1a1a", mb: 3 }}>
          How Our Booking Works
        </Typography>

        <Grid container spacing={2}>
          {[
            { Icon: MdAssignment, color: themeOrange, bg: "#FFF0EB", title: "Describe your task", desc: "Tell us what you need done and pick a date and time." },
            { Icon: MdPeople, color: themeOrange, bg: "#FFF0EB", title: "Get matched", desc: "Connect with a trusted Certified Done Pro in your area." },
            { Icon: MdCheckCircle, color: themeOrange, bg: "#FFF0EB", title: "Get it done", desc: "Your Pro handles the job. Sit back and relax." },
          ].map((step, i) => (
            <Grid item xs={4} key={i}>
              <Stack
                sx={{
                  backgroundColor: step.bg,
                  borderRadius: "14px",
                  p: 2.5,
                  height: "100%",
                }}
                gap={1.5}
              >
                <Box sx={{
                  width: 44, height: 44, borderRadius: "12px",
                  backgroundColor: "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                }}>
                  <step.Icon size={22} color={step.color} />
                </Box>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1a1a1a" }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666", lineHeight: 1.6 }}>
                  {step.desc}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Certified Done Pros */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "#1a1a1a",
            mb: 2,
          }}
        >
          Certified Done Pros
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "#666",
            lineHeight: 1.7,
          }}
        >
          All Done Pros are insured and qualified, highly rated by our customers, and background-checked. Pros must maintain a minimum 90% approval rating from customers.
        </Typography>
      </Box>

      {/* Choose Your Plan */}
      <Typography
        sx={{
          fontSize: { xs: "22px", md: "28px" },
          fontWeight: 700,
          color: "#1a1a1a",
          textAlign: "center",
          mb: 2,
        }}
      >
        Choose Your Plan
      </Typography>

      {/* Pricing Cards - Side by Side */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        {/* Member Card */}
        <Stack
          onClick={() => _handleCardSelection("lower")}
          sx={{
            flex: 1,
            borderRadius: "12px",
            border: `3px solid ${selectedCard === "lower" ? themeOrange : "transparent"}`,
            background: "linear-gradient(180deg, #162f77 0%, #031444 100%)",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" py={1.2} px={2} sx={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
            <Stack direction="row" alignItems="center">
              <img src={logo_white} style={{ width: "32px", objectFit: "contain" }} alt="Logo" />
              <Typography sx={{ color: themeOrange, fontSize: "13px", fontWeight: 700, ml: 0.5 }}>+ Member</Typography>
            </Stack>
            <IconButton sx={{ backgroundColor: "white", height: "22px", width: "22px", borderRadius: "100%", padding: "0px", color: themeOrange, border: `2.5px solid ${themeOrange}` }}>
              {selectedCard === "lower" && <GoCheckCircleFill size={18} />}
            </IconButton>
          </Stack>
          <Stack alignItems="center" py={2.5} px={2} gap={0.5}>
            <Stack direction="row" alignItems="baseline" gap={1}>
              <Typography sx={{ fontSize: "13px", color: "grey", textDecoration: "line-through" }}>${servicePrice}</Typography>
              <Typography sx={{ fontSize: "28px", fontWeight: 800, color: themeOrange }}>${memberPrice}</Typography>
            </Stack>
            <Typography sx={{ fontSize: "13px", color: "white", fontWeight: 500, textAlign: "center" }}>
              {get_service_by_id_data?.service?.additional_text || "For the first visit"}
            </Typography>
            {get_service_by_id_data?.service?.add_price && (
              <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", textAlign: "center" }}>
                +<span style={{ color: themeOrange }}>${get_service_by_id_data?.service?.add_price}</span> {get_service_by_id_data?.service?.additional_text_2 || "for each additional"}
              </Typography>
            )}
          </Stack>
          <Stack px={2} py={1.5} sx={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", textAlign: "center", lineHeight: 1.5 }}>
              Only $10/mo, members get $25 off every job, you save more!
            </Typography>
          </Stack>
        </Stack>

        {/* Regular Card */}
        <Stack
          onClick={() => _handleCardSelection("upper")}
          sx={{
            flex: 1,
            borderRadius: "12px",
            border: `3px solid ${selectedCard === "upper" ? themeOrange : "transparent"}`,
            backgroundColor: "#fff",
            overflow: "hidden",
            cursor: "pointer",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" py={1.2} px={2} sx={{ borderBottom: "1px solid #eee" }}>
            <Typography sx={{ fontSize: "14px", color: "#1a1a1a", fontWeight: 700 }}>Regular</Typography>
            <IconButton sx={{ backgroundColor: "white", height: "22px", width: "22px", borderRadius: "100%", padding: "0px", color: themeOrange, border: `2.5px solid ${themeOrange}` }}>
              {selectedCard === "upper" && <GoCheckCircleFill size={18} />}
            </IconButton>
          </Stack>
          <Stack alignItems="center" py={2.5} px={2} gap={0.5}>
            <Typography sx={{ fontSize: "28px", fontWeight: 800, color: themeOrange }}>${servicePrice}</Typography>
            <Typography sx={{ fontSize: "13px", color: "#333", fontWeight: 500, textAlign: "center" }}>
              {get_service_by_id_data?.service?.additional_text || "For the first visit"}
            </Typography>
            {get_service_by_id_data?.service?.add_price && (
              <Typography sx={{ fontSize: "12px", color: "#888", textAlign: "center" }}>
                +<span style={{ color: themeOrange }}>${get_service_by_id_data?.service?.add_price}</span> {get_service_by_id_data?.service?.additional_text_2 || "for each additional"}
              </Typography>
            )}
          </Stack>
          <Stack px={2} py={1.5} sx={{ borderTop: "1px solid #eee" }}>
            <Typography sx={{ fontSize: "11px", color: "#888", textAlign: "center", lineHeight: 1.5 }}>
              Standard pricing without membership. No monthly commitment.
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Questions about rates */}
      <Typography sx={{ fontSize: "13px", color: "#666", textAlign: "center", mb: 2 }}>
        Questions about our rates?{" "}
        <span
          onClick={() => setOpenRatesModal(true)}
          style={{ color: themeOrange, cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
        >
          Learn more
        </span>
      </Typography>

      {/* Location / Address Bar */}
      {jobs_steps === null && user?.user_role !== 2 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#999", mb: 1 }}>
            Where do you need a Done?
          </Typography>
          <CustomLocationSearch textCondition={false} setSelectedLocationProps={_handleSelectedLocation} />
          {locationErr && (
            <Typography sx={{ fontSize: "12px", color: "#d32f2f", mt: 0.5 }}>{locationErr}</Typography>
          )}
        </Box>
      )}

      {/* CTA Button */}
      {jobs_steps === null && (
        <>
          {user && user?.user_role === 2 ? (
            <Box sx={{ p: 2, backgroundColor: "#fff8e1", borderRadius: "8px", border: "1px solid #ff9800", mb: 3 }}>
              <Typography sx={{ color: "#6a6a6a", fontSize: "14px" }}>
                You must be a registered user to book this service.
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={_handleClick}
              sx={{
                background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                color: "white",
                fontSize: "16px",
                fontWeight: 600,
                padding: "14px 28px",
                borderRadius: "50px",
                textAlign: "center",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(241, 90, 36, 0.3)",
                mb: 3,
              }}
            >
              Continue
            </Box>
          )}
        </>
      )}

      {/* Rates Info Modal */}
      <CustomModal
        isOpen={openRatesModal}
        setIsOpen={() => setOpenRatesModal(!openRatesModal)}
        modal_inner_Style={{ width: { xl: "35vw", lg: "45vw", md: "55vw", sm: "75vw", xs: "90vw" } }}
      >
        <Stack sx={{ backgroundColor: "white", borderRadius: "14px", p: 3, maxHeight: "80vh", overflow: "auto" }}>
          <Stack alignItems="flex-end">
            <IconButton onClick={() => setOpenRatesModal(false)}>
              <RxCross2 size={20} />
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", textAlign: "center", mb: 2 }}>
            Questions about our rates?
          </Typography>
          <Stack gap={2.5}>
            <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.7 }}>
              We've already done the work of crafting unusual Done rates and job minimums are based on industry averages per category.
            </Typography>
            <Stack gap={0.5}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1a1a1a" }}>Have a large job? 🏠</Typography>
              <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.7 }}>
                Our professionals are happy to provide a free, flat-rate quote. Simply request that you'd like a free quote in the job description. You will still be required to submit the request, but rest assured nothing is charged until after the job is complete.
              </Typography>
            </Stack>
            <Stack gap={0.5}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1a1a1a" }}>What happens next? 🔍</Typography>
              <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.7 }}>
                Once your job is accepted, your professional will be in touch, and if possible, will give a quick idea of scope prior to arrival. Keep in mind it is often difficult to determine the true scope of a job until being on-site.
              </Typography>
            </Stack>
            <Stack gap={0.5}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1a1a1a" }}>Need to cancel? No problem! ❌</Typography>
              <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.7 }}>
                If for any reason you need to cancel after the pre-service discussion and/or their estimate - you can cancel up until the moment they've left for the appointment at no charge.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CustomModal>

      {/* Vendor Warning Modal */}
      <CustomModal
        isOpen={openVendorModal}
        setIsOpen={() => setOpenVendorModal(!openVendorModal)}
        modal_inner_Style={{ width: { xl: "30vw", lg: "40vw", md: "50vw", sm: "70vw", xs: "90vw" } }}
      >
        <Stack sx={{ backgroundColor: "white", borderRadius: "10px", p: 2 }}>
          <Stack alignItems="flex-end">
            <IconButton onClick={() => setOpenVendorModal(!openVendorModal)}>
              <RxCross2 size={20} />
            </IconButton>
          </Stack>
          <Stack gap={2} px={2} pb={2}>
            <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
              You must be a registered user to book this service.
            </Typography>
            <Stack direction="row" justifyContent="flex-end">
              <ButtonComp
                onClick={() => setOpenVendorModal(false)}
                label="Ok"
                style={{ width: "auto", borderRadius: "15px", background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)" }}
              />
            </Stack>
          </Stack>
        </Stack>
      </CustomModal>

      {/* Membership Modal */}
      <CustomModal
        isOpen={openMemberShipModal}
        setIsOpen={() => setOpenMemberShipModal(!openMemberShipModal)}
        modal_inner_Style={{ width: { xl: "auto", lg: "auto", md: "auto", sm: "auto", xs: "85vw" } }}
      >
        <Stack
          sx={{
            overflow: "auto",
            background: "linear-gradient(180deg, #273350, #2f4f94)",
            borderRadius: "10px",
          }}
          alignItems="center"
          p={1}
        >
          <Stack sx={{ width: "100%" }} alignItems="flex-end">
            <IconButton onClick={() => setOpenMemberShipModal(!openMemberShipModal)}>
              <RxCross2 size={20} style={{ color: "white" }} />
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{ width: "100%", padding: "35px 24px" }}>
            <Stack alignItems="center">
              <Typography sx={{ color: "white", fontSize: "15px", fontWeight: 400 }}>
                Thanks for choosing <span style={{ color: themeOrange, fontWeight: 500 }}>Done+</span>!
              </Typography>
              <Typography sx={{ color: "white", fontSize: "15px", fontWeight: 300 }}>
                Membership for just
              </Typography>
              <Typography sx={{ color: themeOrange, fontSize: "15px", fontWeight: 400 }}>
                $10/month. Cancel anytime.
              </Typography>
            </Stack>

            <Stack sx={{ borderRadius: "7px", backgroundColor: "rgba(0,0,0,.25)", padding: "10px 15px" }}>
              <Stack gap={1} direction="row" alignItems="center">
                <FaCheck style={{ color: themeOrange, fontSize: "16px" }} />
                <Typography sx={{ color: "white", fontSize: "13px", fontWeight: 200 }}>
                  Save $25 on every Done Job
                </Typography>
              </Stack>
              <Stack gap={1} direction="row" alignItems="center">
                <FaCheck style={{ color: themeOrange, fontSize: "16px" }} />
                <Typography sx={{ color: "white", fontSize: "13px", fontWeight: 200 }}>
                  Extended Warranty
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ textAlign: "center", color: "white", fontSize: "11px", fontWeight: 200 }}>
              By signing up, you agree to the Done+ Terms and authorize us to charge your default payment method on file.
            </Typography>

            <Stack gap={2}>
              <ButtonComp
                onClick={() => setOpenMemberShipModal(false)}
                label="Go Back"
                className="member_ship_go_back_btn"
              />
              <ButtonComp
                onClick={() => { setOpenMemberShipModal(false); dispatch(setJobsSteps({ value: "1" })); }}
                label="Agree & Continue"
                className="member_ship_agree_continue_btn"
              />
            </Stack>
          </Stack>
        </Stack>
      </CustomModal>
    </Stack>
  );
};

export default FirstCard;
